/**
 * ============================================================================
 * UserManagementPage — 사용자 관리 (CRUD + 역할할당 + 비밀번호초기화 + 잠금해제)
 * ============================================================================
 *
 * 【역할】
 *   시스템 사용자(fw_user) 마스터의 전반 관리 화면. 관리자 전용.
 *   기본 CRUD 외에 역할 M:M 매핑, 비밀번호 강제 초기화, 계정잠금 해제까지
 *   한 화면에서 통합 처리한다.
 *
 * 【주요 기능】
 *   - 검색: userName / department 키워드 필터
 *   - CRUD: 추가/수정 Modal, 삭제(soft), 벌크 저장
 *   - 역할 할당: 선택 사용자의 역할 M:M 편집 (Checkbox.Group + assigned Y/N)
 *   - 비밀번호 초기화: 임시 비밀번호 발급 (서버에서 메일/SMS 발송 가능)
 *   - 계정잠금 해제: accountLocked='N' 으로 갱신 후 재조회
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=adminUser/searchList)      — 사용자 목록
 *   - POST /api/v1/transaction (service=adminUser/save)            — 사용자 벌크 저장
 *   - POST /api/v1/transaction (service=adminUser/searchUserRoles) — 선택 사용자의 역할
 *   - POST /api/v1/transaction (service=adminUser/saveUserRoles)   — 역할 M:M 저장
 *   - POST /api/v1/transaction (service=adminUser/resetPassword)   — 비밀번호 초기화
 *   - POST /api/v1/transaction (service=adminUser/unlockAccount)   — 잠금 해제
 *
 * 【DataSet/Hook】
 *   - useDataSet<UserRow>() — ds     : 사용자 목록 (PK: userId)
 *   - useDataSet<RoleRow>() — roleDs : 역할 매핑 (PK: roleId, assigned 'Y'/'N')
 *   - useTransaction()      — RPC 통합 호출
 *   - useLabel()            — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/admin/user', element: <UserManagementPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageUserManagement.vue`
 * 【의존성】 antd Button/Form/Input/Modal/Switch/Tag/Checkbox/Space, framework hooks
 */
import { useEffect, useMemo, useState } from 'react'
import { Button, Form, Input, Modal, Space, Switch, Tag, Checkbox } from 'antd'
import CrudToolbar from '@/components/common/CrudToolbar'
import SearchPanel from '@/components/common/SearchPanel'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'

/** 사용자 그리드 1행. */
interface UserRow {
  /** 내부 사용자 PK (DB 시퀀스, 신규 시 undefined). */
  userId?: string
  /** 로그인 ID — 비즈니스 unique 키 (예: "alice"). */
  loginId: string
  /** 사용자 표시명 (예: "이앨리스"). */
  userName: string
  /** 부서명. */
  department?: string
  /** 이메일 — 비밀번호 초기화 메일 발송 대상. */
  email?: string
  /** 사용여부 ('Y'=활성 / 'N'=비활성). */
  useYn: 'Y' | 'N'
  /** 계정잠금 여부 ('Y'=잠김 / 'N'=정상). 5회 로그인 실패 시 자동 'Y'. */
  accountLocked?: 'Y' | 'N'
  /** 대표 역할명 (서버 join 결과, 표시 전용). */
  roleName?: string
  /** 개발자 권한 (1=개발자 / 0=일반) — 모바일 개발자 옵션 게이트. */
  isDeveloper?: number
}

/** 역할 매핑 1행 — 선택 사용자에 대한 역할 할당 상태. */
interface RoleRow {
  /** 역할 PK. */
  roleId: string
  /** 역할 표시명. */
  roleName: string
  /** 현재 사용자에게 할당 여부 ('Y'=할당 / 'N'=미할당). */
  assigned?: 'Y' | 'N'
}

/**
 * UserManagementPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function UserManagementPage() {
  const ds = useDataSet<UserRow>()
  const roleDs = useDataSet<RoleRow>()
  const { transaction } = useTransaction()
  const [searchForm] = Form.useForm()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingIdx, setEditingIdx] = useState<number>(-1)
  const [form] = Form.useForm()
  const [roleModalOpen, setRoleModalOpen] = useState(false)
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([])

  /**
   * 사용자 검색 — adminUser/searchList → ds.
   * @param values 외부 폼 값 (생략 시 내부 searchForm 사용)
   */
  const search = (values?: Record<string, unknown>) =>
    transaction({
      id: 'search',
      service: 'adminUser/searchList',
      inds: { ds_search: values ?? searchForm.getFieldsValue() },
      outds: { ds_userList: ds }
    })

  useEffect(() => {
    search()
  }, [])

  /** 추가 Modal open — 신규 모드(editingIdx=-1), useYn 기본값 'Y'. */
  const openAdd = () => {
    setEditingIdx(-1)
    form.resetFields()
    form.setFieldsValue({ useYn: 'Y', isDeveloper: 0 })
    setModalOpen(true)
  }
  /** 수정 Modal open — 선택행이 있어야 동작. loginId 는 disable. */
  const openEdit = () => {
    const idx = ds.selectedIndex
    if (idx < 0) return
    setEditingIdx(idx)
    form.setFieldsValue(ds.rows[idx].data)
    setModalOpen(true)
  }
  /** 선택 사용자 삭제 — DataSet C/U/D 추적 (C: 즉시 / U: 'D' 마킹). */
  const del = () => {
    if (ds.selectedIndex >= 0) ds.deleteRow(ds.selectedIndex)
  }
  /** Modal 제출 — 신규 모드면 addRow, 수정 모드면 updateRow. */
  const onSubmit = (values: UserRow) => {
    if (editingIdx < 0) ds.addRow(values)
    else ds.updateRow(editingIdx, values)
    setModalOpen(false)
  }
  /** 사용자 벌크 저장 — adminUser/save (C/U/D 변경분만). 성공 시 재조회. */
  const save = async () => {
    const res = await transaction({
      id: 'save',
      service: 'adminUser/save',
      action: 'save',
      inds: { ds_userList: ds },
      outds: { ds_userList: ds }
    })
    if (res.success) search()
  }

  /**
   * 역할 할당 Modal open.
   * - 1) 선택 사용자의 역할 매핑 조회 → roleDs
   * - 2) assigned='Y' 행만 selectedRoleIds 로 추출 (Checkbox.Group value 동기화)
   * - 3) Modal open
   */
  const openRoles = async () => {
    const idx = ds.selectedIndex
    if (idx < 0) return
    const user = ds.rows[idx].data
    const res = await transaction({
      id: 'searchRoles',
      service: 'adminUser/searchUserRoles',
      inds: { ds_search: { userId: user.userId ?? user.loginId } },
      outds: { ds_roleList: roleDs }
    })
    if (res.success) {
      setSelectedRoleIds(
        roleDs.rows.filter((r) => r.data.assigned === 'Y').map((r) => r.data.roleId)
      )
      setRoleModalOpen(true)
    }
  }

  /**
   * 역할 매핑 저장.
   * - selectedRoleIds 기준으로 assigned 'Y'/'N' 재계산 후 ds_roleList 로 전송.
   * - 서버는 기존 매핑과 diff 하여 INSERT/DELETE 처리.
   */
  const saveRoles = async () => {
    const idx = ds.selectedIndex
    if (idx < 0) return
    const user = ds.rows[idx].data
    await transaction({
      id: 'saveRoles',
      service: 'adminUser/saveUserRoles',
      action: 'save',
      inds: {
        ds_search: { userId: user.userId ?? user.loginId },
        ds_roleList: roleDs.rows.map((r) => ({
          ...r.data,
          assigned: selectedRoleIds.includes(r.data.roleId) ? 'Y' : 'N'
        }))
      }
    })
    setRoleModalOpen(false)
  }

  /**
   * 비밀번호 초기화 — adminUser/resetPassword.
   * 서버에서 임시 비밀번호 발급 + 등록된 email 로 안내 발송 (구현 시).
   */
  const resetPw = async () => {
    const idx = ds.selectedIndex
    if (idx < 0) return
    await transaction({
      id: 'resetPassword',
      service: 'adminUser/resetPassword',
      action: 'save',
      inds: { ds_search: { userId: ds.rows[idx].data.userId ?? ds.rows[idx].data.loginId } }
    })
  }
  /** 계정잠금 해제 — adminUser/unlockAccount → accountLocked='N'. 후 재조회. */
  const unlock = async () => {
    const idx = ds.selectedIndex
    if (idx < 0) return
    await transaction({
      id: 'unlockAccount',
      service: 'adminUser/unlockAccount',
      action: 'save',
      inds: { ds_search: { userId: ds.rows[idx].data.userId ?? ds.rows[idx].data.loginId } }
    })
    search()
  }

  const { t } = useLabel()
  const columns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<UserRow>>[] = [
      { key: 'COL_LOGIN_ID', dataIndex: ['data', 'loginId'], width: 120 },
      { key: 'COL_USER_NAME', dataIndex: ['data', 'userName'], width: 120 },
      { key: 'COL_DEPT', dataIndex: ['data', 'department'], width: 120 },
      { key: 'COL_EMAIL', dataIndex: ['data', 'email'], width: 200 },
      { key: 'COL_ROLE_NAME', dataIndex: ['data', 'roleName'], width: 140 },
      {
        key: 'COL_USE_YN',
        dataIndex: ['data', 'useYn'],
        width: 80,
        render: (v) => (
          <Tag color={v === 'Y' ? 'green' : 'red'}>
            {v === 'Y' ? t('LBL_YES', '사용') : t('LBL_NO', '미사용')}
          </Tag>
        )
      },
      {
        key: 'LBL_IS_DEVELOPER',
        dataIndex: ['data', 'isDeveloper'],
        width: 90,
        render: (v) =>
          Number(v) === 1 ? <Tag color="blue">{t('LBL_IS_DEVELOPER', '개발자')}</Tag> : '-'
      },
      {
        key: 'COL_LOCKED',
        dataIndex: ['data', 'accountLocked'],
        width: 70,
        render: (v) => (v === 'Y' ? <Tag color="red">{t('COL_LOCKED', '잠김')}</Tag> : '-')
      },
      {
        key: 'COL_STATUS',
        dataIndex: '_state',
        width: 70,
        render: (s) =>
          s === 'C' ? (
            <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
          ) : s === 'U' ? (
            <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
          ) : null
      }
    ]
    return makeColumns(specs, t)
  }, [t])

  return (
    <PageContainer>
      <SearchPanel form={searchForm} onSearch={(v) => search(v)}>
        <Form.Item label={t('COL_USER_NAME', '사용자명')} name="userName">
          <FormInput />
        </Form.Item>
        <Form.Item label={t('COL_DEPT', '부서')} name="department">
          <FormInput />
        </Form.Item>
      </SearchPanel>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          <CrudToolbar
            onSearch={() => search()}
            onAdd={openAdd}
            onEdit={openEdit}
            onDelete={del}
            onSave={save}
            dirty={ds.isDirty}
          />
          <Button onClick={openRoles}>{t('BTN_ROLE_ASSIGN', '역할 할당')}</Button>
          <Button onClick={resetPw}>{t('BTN_RESET_PASSWORD', '비밀번호 초기화')}</Button>
          <Button onClick={unlock}>{t('BTN_UNLOCK', '잠금 해제')}</Button>
        </Space>
      </div>
      <DataTable<TrackedRow<UserRow>>
        rowKey="_rowId"
        dataSource={ds.getVisibleRows()}
        columns={columns}
        pagination={false}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: ds.selectedRow ? [ds.selectedRow._rowId] : [],
          onChange: (keys) => {
            const idx = ds.rows.findIndex((r) => r._rowId === keys[0])
            ds.setSelectedIndex(idx)
          }
        }}
      />

      <Modal
        title={editingIdx < 0 ? t('TTL_ADD', '사용자 추가') : t('TTL_EDIT', '사용자 수정')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            label={t('COL_LOGIN_ID', '로그인ID')}
            name="loginId"
            rules={[{ required: true }]}
          >
            <Input disabled={editingIdx >= 0} />
          </Form.Item>
          <Form.Item
            label={t('COL_USER_NAME', '사용자명')}
            name="userName"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('COL_DEPT', '부서')} name="department">
            <Input />
          </Form.Item>
          <Form.Item label={t('COL_EMAIL', '이메일')} name="email">
            <Input />
          </Form.Item>
          <Form.Item
            label={t('LBL_USE_YN', '사용여부')}
            name="useYn"
            getValueFromEvent={(v) => (v ? 'Y' : 'N')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label={t('LBL_IS_DEVELOPER', '개발자 권한')}
            name="isDeveloper"
            getValueFromEvent={(v) => (v ? 1 : 0)}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('BTN_ROLE_ASSIGN', '역할 할당')}
        open={roleModalOpen}
        onCancel={() => setRoleModalOpen(false)}
        onOk={saveRoles}
        width={480}
      >
        <Checkbox.Group
          value={selectedRoleIds}
          onChange={(v) => setSelectedRoleIds(v as string[])}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {roleDs.rows.map((r) => (
            <Checkbox key={r.data.roleId} value={r.data.roleId}>
              {r.data.roleName} <span style={{ color: '#888' }}>({r.data.roleId})</span>
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Modal>
    </PageContainer>
  )
}
